import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Layers,
  ArrowRight,
  Play,
  FolderOpen,
  Download,
  ExternalLink,
  BookOpen,
  Sparkles,
  Plug,
} from 'lucide-react'
import { FreeCutLogo } from '@/components/brand/freecut-logo'
import { ColorThemeToggle } from '@/components/color-theme-toggle'
import { Button } from '@/components/ui/button'
import { authConfig } from '@/features/auth/config'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { AuthUserMenu } from '@/features/auth/components/auth-user-menu'
import { WhatsNewDialog } from '@/features/editor/components/whats-new-dialog'
import { hasUnseenChangelog } from '@/features/editor/components/whats-new-seen'
import { LocalMcpStatusPanel } from '@/features/settings/components/local-mcp-status-panel'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

export const Route = createFileRoute('/')({
  component: LandingPage,
})

const faqItems = [
  {
    question: '快剪是免费的吗？',
    answer:
      '可以直接免费开始用。当前核心剪辑链路、AI 字幕、AI 配音、AI 背景音乐等能力都已经开放体验，没有任何次数限制。后续会员能力会单独扩展，但基础工作流现在就能直接开工。',
  },
  {
    question: '需要安装客户端吗？',
    answer:
      '不需要。快剪直接在浏览器里运行，打开页面后就可以开始剪辑。',
  },
  {
    question: '视频和项目文件保存在哪里？',
    answer:
      '项目数据会优先保存在你的本地工作区，素材也会尽量走本地读写，不会默认上传到远端。',
  },
  {
    id: 'browser-support',
    question: '推荐使用什么浏览器？',
    answer: (
      <>
        <p className="mb-3">
          当前更推荐使用最新版 Chrome 或 Edge。快剪依赖 WebGPU、WebCodecs、OPFS 和文件系统访问等现代浏览器能力，因此 Chromium 内核体验会更完整。
        </p>
        <p>
          <strong>Brave 用户：</strong> 文件系统访问能力默认可能是关闭的。需要手动打开时，请进入{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">
            brave://flags/#file-system-access-api
          </code>
          ，将其设为 <strong>已启用</strong> 后重启浏览器。
        </p>
      </>
    ),
  },
  {
    question: '支持导出哪些格式？',
    answer:
      '视频支持 MP4、MOV、WebM、MKV，音频支持 MP3、AAC、WAV。当前导出面板也已经支持多种编码与质量预设。',
  },
  {
    id: 'mcp-automation',
    question: '什么是 MCP 自动化？怎么用？',
    answer: (
      <>
        <p className="mb-3">
          MCP 自动化让 Codex、ZCode、Cursor 等支持 MCP 协议的 AI 客户端，直接读取你当前在浏览器里打开的 FastCut 项目，并调用剪辑工具自动修改时间线。整个过程都在本地完成，不需要手动导出、导入或上传素材。
        </p>
        <p>
          点击页面顶部的 MCP 按钮即可查看连接状态、复制配置，或把配置一键交给 AI 客户端。更完整的安装与配置说明可查看{' '}
          <Link to="/docs/$slug" params={{ slug: 'mcp-automation' }} className="text-primary underline-offset-4 hover:underline">
            MCP 自动化教程
          </Link>
          。
        </p>
      </>
    ),
  },
  {
    question: '接下来会继续补哪些能力？',
    answer:
      '接下来会继续强化 AI 工作流，支持MCP，包括更稳的 AI 配音、音色克隆、字幕生成、背景音乐生成，以及更完整的中文界面、导出体验和项目管理能力。',
  },
]

const showcaseItems = [
  {
    id: 'timeline',
    title: '时间线剪辑',
    description: '视频、音频、字幕、图形多轨并行，直接按成片节奏组织素材',
    icon: Layers,
    media: '/assets/landing/timeline.png',
    className: 'md:col-span-2 md:row-span-1',
    aspectClass: 'aspect-[2/1]',
  },
  {
    id: 'keyframe',
    title: '关键帧动画',
    description: '位移、缩放、透明度与节奏变化都能精细控制',
    icon: Play,
    media: '/assets/landing/keyframe.png',
    className: 'md:row-span-2',
    aspectClass: 'aspect-[3/4] md:aspect-auto md:h-full',
  },
  {
    id: 'projects',
    title: '项目管理',
    description: '本地工作区管理项目、素材、缓存与代理，结构清晰可控',
    icon: FolderOpen,
    media: '/assets/landing/projects.png',
    className: '',
    aspectClass: 'aspect-video',
  },
  {
    id: 'export',
    title: '本地导出',
    description: '不依赖本地客户端，直接在浏览器里完成渲染与导出',
    icon: Download,
    media: '/assets/landing/export.png',
    className: '',
    aspectClass: 'aspect-video',
  },
]

function LandingPage() {
  const isAuthenticated = useAuthSessionStore((state) => state.isAuthenticated)
  const [showWhatsNewDialog, setShowWhatsNewDialog] = useState(false)
  const [hasUnseenWhatsNew, setHasUnseenWhatsNew] = useState(false)
  const [showMcpDialog, setShowMcpDialog] = useState(false)

  useEffect(() => {
    setHasUnseenWhatsNew(hasUnseenChangelog())
  }, [])

  const openWhatsNew = () => {
    setHasUnseenWhatsNew(false)
    setShowWhatsNewDialog(true)
  }

  return (
    <div className="min-h-screen bg-background text-foreground select-text">
      <WhatsNewDialog open={showWhatsNewDialog} onOpenChange={setShowWhatsNewDialog} />

      <Dialog open={showMcpDialog} onOpenChange={setShowMcpDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>MCP 自动化</DialogTitle>
            <DialogDescription>
              将 Codex、ZCode、Cursor 等 AI 客户端连接到当前 FastCut 项目，自动执行剪辑操作。
            </DialogDescription>
          </DialogHeader>
          <LocalMcpStatusPanel />
        </DialogContent>
      </Dialog>

      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <FreeCutLogo size="sm" />
            <span className="text-sm font-medium text-muted-foreground">AI 视频工作台</span>
          </Link>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" asChild>
              <Link to="/docs" data-tooltip="用户指南" data-tooltip-side="bottom" aria-label="用户指南">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5A2.5 2.5 0 0 1 4 19.5Z" />
                  <path d="M8 7h8" /><path d="M8 11h6" />
                </svg>
              </Link>
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => setShowMcpDialog(true)}
              data-tooltip="MCP 自动化"
              data-tooltip-side="bottom"
              aria-label="MCP 自动化"
            >
              <Plug className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="relative h-8 w-8 rounded-full"
              onClick={openWhatsNew}
              data-tooltip="更新说明"
              data-tooltip-side="bottom"
              aria-label="更新说明"
            >
              <Sparkles className="h-4 w-4" />
              {hasUnseenWhatsNew && (
                <span
                  className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-primary"
                  aria-hidden="true"
                />
              )}
            </Button>

            <ColorThemeToggle />

            {isAuthenticated() ? (
              <AuthUserMenu />
            ) : (
              <Button asChild size="sm">
                <Link to="/login">登录</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border/60 px-6 py-12 sm:py-16 lg:py-20">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-20 top-8 h-[360px] w-[360px] rounded-full bg-primary/12 blur-3xl" />
          <div className="absolute right-0 top-1/3 h-[420px] w-[420px] rounded-full bg-primary/8 blur-3xl" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent" />
        </div>

        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1.2fr)] lg:gap-12">
          <div className="animate-fade-in text-center lg:text-left">
            <div className="mb-6 flex items-center justify-center gap-3 lg:justify-start">
              <FreeCutLogo size="lg" />
              <span className="rounded-full border border-primary/20 bg-primary/12 px-3 py-1 text-xs font-medium text-primary">
                快剪工作台
              </span>
            </div>

            <h1 className="mb-5 max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              AI 智能代理驱动的专业剪辑工作台，<span className="text-primary">从想法直接推进到成片。</span>
            </h1>

            <p className="mb-6 max-w-3xl text-base text-muted-foreground sm:text-lg lg:max-w-2xl">
              不只是把剪辑工具搬进浏览器。现在你可以直接调用 AI 智能助理先生成计划、自动推进配音、字幕、背景音乐和草稿编排，
              还可以接着用文生图补画面、再配合多轨时间线、本地工作区和高质量导出，把口播、短视频和成片流程一次打通。
              更进一步，全新的 MCP 自动化能让 Codex、ZCode、Cursor 等 AI 客户端直接接管你当前打开的项目，全程在本地完成自动剪辑。
            </p>

            <div className="mb-8 flex max-w-3xl flex-wrap items-center justify-center gap-2 text-xs sm:text-sm lg:justify-start">
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                免费开始使用
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                隐私安全
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                操作比剪映还简单
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                桌面级的性能表现
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                AI 智能代理
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                MCP 自动化
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                AI 配音
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                文生图片
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                AI 音色克隆
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                AI 生成字幕
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                AI 背景音乐
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                多轨时间线
              </span>
              <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                本地超高质量导出
              </span>
            </div>

            <p className="mb-6 max-w-2xl rounded-2xl border border-primary/20 bg-primary/8 px-4 py-3 text-sm text-primary/90">
              现在就能直接体验完整工作台。AI 智能代理、文生图、配音和时间线已经接成一条真实可用的创作链路，还支持通过 MCP 让外部 AI 客户端自动剪辑。建议使用最新版 Chrome 或 Edge，以获得最完整的文件访问、AI 能力和导出体验。
            </p>

            <div className="flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start">
              <Button asChild size="lg" className="gap-2 px-8">
                <Link to="/projects">
                  {isAuthenticated() ? '进入项目中心开始剪辑' : '立即开始体验'}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
{/* 
            {isAuthenticated() ? null : (
              <>
                <Button asChild variant="outline" size="lg">
                  <Link to="/login">登录</Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/register">注册</Link>
                </Button>
              </>
            )} */}

              {authConfig.mainSiteUrl ? (
                <Button asChild variant="outline" size="lg" className="gap-2">
                  <a href={authConfig.mainSiteUrl}>返回主站</a>
                </Button>
              ) : null}

              <Button asChild variant="outline" size="lg" className="gap-2">
                <Link to="/docs">
                  <BookOpen className="h-4 w-4" />
                  用户指南
                </Link>
              </Button>
            </div>
          </div>

          <div className="animate-fade-in [animation-delay:120ms]">
            <div className="relative mx-auto max-w-4xl">
              <div className="absolute -inset-4 rounded-[2rem] bg-primary/12 blur-2xl" />
              <div className="relative overflow-hidden rounded-[1.75rem] border border-primary/20 bg-card/80 shadow-[0_30px_120px_rgba(0,0,0,0.45)] backdrop-blur">
                <div className="flex items-center justify-between border-b border-border/60 bg-background/85 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">FastCut 工作台预览</div>
                    <div className="text-xs text-muted-foreground">智能代理、文生图与时间线剪辑已经并到同一个工作台里</div>
                  </div>
                  <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-medium text-primary">
                    Agent + AI + 剪辑一体化
                  </div>
                </div>

                <div className="relative bg-black">
                  <img
                    src="/assets/landing/bg.png"
                    alt="FastCut 工作台整体布局预览"
                    className="block h-auto w-full object-cover"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent" />
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-border/70 bg-card/70 px-4 py-3">
                  <div className="text-[11px] uppercase tracking-wide text-primary">左侧</div>
                  <div className="mt-1 text-sm font-medium">素材、项目、AI 工具与智能代理</div>
                  <div className="mt-1 text-xs text-muted-foreground">导入素材后，可以直接让助理生成初稿，或者继续衔接字幕、配音、文生图和背景音乐工作流。</div>
                </div>
                <div className="rounded-2xl border border-border/70 bg-card/70 px-4 py-3">
                  <div className="text-[11px] uppercase tracking-wide text-primary">中间</div>
                  <div className="mt-1 text-sm font-medium">预览区 + 多轨时间线</div>
                  <div className="mt-1 text-xs text-muted-foreground">智能代理生成的内容不会停在对话里，字幕、音乐、配音和画面都能直接落到同一条时间线上继续编辑。</div>
                </div>
                <div className="rounded-2xl border border-border/70 bg-card/70 px-4 py-3">
                  <div className="text-[11px] uppercase tracking-wide text-primary">右侧</div>
                  <div className="mt-1 text-sm font-medium">属性与导出操作</div>
                  <div className="mt-1 text-xs text-muted-foreground">助理先帮你把初稿搭起来，右侧属性区再负责精修和导出，不需要在多个工具之间来回补最后一步。</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase Bento Grid */}
      <section className="border-t border-border px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              从 AI 生成到导出成片，核心流程已经接通
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              从素材导入到根据提示词生成你想要的内容，把 AI 配音、音色克隆、字幕、背景音乐和时间线剪辑真正接成一条能落地的工作流。
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3 md:grid-rows-2">
            {showcaseItems.map((item) => (
              <div
                key={item.id}
                className={`group relative overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 ${item.className}`}
              >
                {/* Media placeholder or actual media */}
                <div className={`relative ${item.aspectClass} w-full overflow-hidden bg-muted`}>
                  {item.media ? (
                    <img
                      src={item.media}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    /* Placeholder with icon */
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex flex-col items-center gap-3 text-muted-foreground/50">
                        <item.icon className="h-12 w-12" />
                        <span className="text-xs uppercase tracking-wider">界面预览</span>
                      </div>
                      {/* Subtle grid pattern */}
                      <div
                        className="absolute inset-0 opacity-[0.03]"
                        style={{
                          backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
                                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
                          backgroundSize: '24px 24px',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Content overlay */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-card via-card/95 to-transparent p-4 pt-8">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                      <item.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{item.title}</h3>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="border-t border-border px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">先看工作台，再决定要不要直接开工</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              AI 功能、时间线、项目区和导出链路都已经能直接跑起来，先看演示会更直观。
            </p>
          </div>

          <a
            href="https://www.bilibili.com/video/BV1nk9fBTE6K/"
            target="_blank"
            rel="noopener noreferrer"
            className="group block overflow-hidden rounded-xl border border-border bg-card shadow-lg transition-colors hover:border-primary/50"
          >
            <div className="relative aspect-video w-full overflow-hidden bg-muted">
              <img
                src="/assets/landing/bg.png"
                alt="快剪工作台整体布局预览"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-white/30 bg-black/55 text-white shadow-2xl backdrop-blur-sm">
                  <Play className="ml-1 h-8 w-8 fill-current" />
                </div>
              </div>
              <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full border border-white/20 bg-black/60 px-3 py-1.5 text-sm text-white backdrop-blur-sm">
                <span>前往 BiliBili 观看演示</span>
                <ExternalLink className="h-3.5 w-3.5" />
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="border-t border-border bg-card/50 px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              常见问题
            </h2>
            <p className="text-muted-foreground">先把免费使用、浏览器要求和 AI 能力这些高频问题说明清楚。</p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} id={item.id}>
                <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border px-6 py-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div>FastCut.video © {new Date().getFullYear()} FastCut 快剪</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm">
            <span>免费开始剪</span>
            <span>AI 视频工作台</span>
            <a href="mailto:mqh0623@gmail.com" className="transition-colors hover:text-foreground">
              商务与建议：mqh0623@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
